import {
  ChangeDetectionStrategy,
  Component,
  inject,
  viewChild,
  signal,
  computed,
  ViewEncapsulation,
  type Signal,
  type WritableSignal,
  type ComponentRef,
  type EmbeddedViewRef,
  type TemplateRef
} from '@angular/core';
import { BasePortalOutlet, CdkPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ANIMATION_CONFIGURATION } from '../../../../domain/animation.domain';
import { ModalOptions } from '../../modal-options';
import type { ModalContainer, SubModalRef } from '../../modal.domain';
import type { ComponentPortal } from '@angular/cdk/portal';

/**
 * Throws an exception for the case when a ComponentPortal is attached to a DomPortalOutlet without an origin.
 */
export const throwContentAlreadyAttachedError = (): void => {
  throw Error('Attempting to attach modal content after content is already attached');
};

/**
 * ModalWindowComponent is a modal container.
 *
 * Note: all input parameters are handled by Material CDK Overlay now and can be controlled via ModalOptions
 * parameters passed to ModalService.open(ComponentType, ModalOptions).
 *
 * @see ../modal-options
 */
@Component({
  selector: 'td-wb-modal',
  templateUrl: 'modal-window.component.html',
  styleUrls: ['modal-window.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkPortalOutlet
  ],
  hostDirectives: [CdkScrollable],
  host: {
    '[class]': 'classes()',
    '[attr.id]': 'id',
    '[attr.role]': 'role()',
    '[attr.tabindex]': '"-1"',
    '[attr.aria-modal]': 'ariaModal()',
    '[attr.aria-label]': 'options.ariaLabel',
    '[attr.aria-labelledby]': 'options.ariaLabelledBy',
    '[attr.aria-describedby]': 'options.ariaDescribedBy',
    '[@.disabled]': 'animationsDisabled',
    '[animate.enter]': '""',
    '[animate.leave]': '"td-wb-modal--leave"'
  }
})
export class ModalWindowComponent extends BasePortalOutlet implements ModalContainer {
  id: string;
  ariaDescribedBy: string | undefined;
  ariaLabeledBy: string | undefined;

  readonly animationsDisabled: boolean = inject(ANIMATION_CONFIGURATION);
  readonly options: ModalOptions = inject(ModalOptions);

  readonly classes: Signal<string> = computed((): string => {
    return ['td-wb-modal', `td-wb-modal--${this.options.size}`, 'td-wb-modal--mobile-full-screen'].join(' ');
  });

  readonly ariaModal: WritableSignal<string | undefined> = signal<string | undefined>('true');
  readonly role: WritableSignal<string | undefined> = signal<string | undefined>(this.options.role);
  readonly subModal: WritableSignal<TemplatePortal<unknown> | undefined> = signal<TemplatePortal<unknown> | undefined>(undefined);

  /** The portal outlet inside this container into which the modal content will be loaded. */
  readonly cdkPortalOutlet: Signal<CdkPortalOutlet> = viewChild.required('portalOutlet', { read: CdkPortalOutlet });

  /**
   * Attach a ComponentPortal as content to this modal container.
   *
   * @param portal Portal to be attached as the modal content.
   */
  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    if (this.cdkPortalOutlet().hasAttached()) {
      throwContentAlreadyAttachedError();
    }
    return this.cdkPortalOutlet().attachComponentPortal(portal);
  }

  /**
   * Attach a TemplatePortal as content to this modal container.
   *
   * @param portal Portal to be attached as the modal content.
   */
  attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
    if (this.cdkPortalOutlet().hasAttached()) {
      throwContentAlreadyAttachedError();
    }
    return this.cdkPortalOutlet().attachTemplatePortal(portal);
  }

  openSubModal(templateRef: TemplateRef<unknown>): SubModalRef {
    this.subModal.set(new TemplatePortal<unknown>(templateRef, undefined));
    return {
      close: (): void => this.closeSubModal()
    };
  }

  toggleScreenReaderVisibility(visible: boolean): void {
    this.ariaModal.set(visible ? 'true' : undefined);
    this.role.set(visible ? 'dialog' : undefined);
  }

  private closeSubModal(): void {
    const subModal = this.subModal();
    if (subModal) {
      subModal.detach();
      this.subModal.set(undefined);
    }
  }
}
